import { useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";
import { SocialAuthButtons } from "@/components/social-auth-buttons";

function friendlyError(error: string): string {
  if (error.includes("INVALID_CREDENTIALS") || error.includes("invalid")) return "Invalid email or password.";
  if (error.includes("ACCOUNT_LOCKED") || error.includes("locked")) return "Account locked. Please contact support.";
  if (error.includes("EMAIL_NOT_VERIFIED")) return "Please verify your email first. Check your inbox.";
  if (error.includes("TOO_MANY_REQUESTS") || error.includes("rate")) return "Too many attempts. Please wait and try again.";
  return "Login failed. Please check your credentials and try again.";
}

const SUCCESS_PREFIX = "\u2709\uFE0F";

function isSuccessMessage(message: string): boolean {
  return message.startsWith(SUCCESS_PREFIX);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const locationState = location.state as { from?: string; message?: string } | null;
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const from = redirectParam ?? locationState?.from ?? "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(locationState?.message ?? null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setIsLoading(true);

    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        setError(friendlyError(result.error.message ?? ""));
      } else {
        const refreshed = await refresh();

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "ready") {
          navigate(from, { replace: true });
          return;
        }

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "missing-organization") {
          setError("Your account is signed in, but your workspace is still finishing setup. Wait a few seconds, then try again.");
          return;
        }

        if (refreshed.status === "authenticated" && refreshed.bootstrapState === "failed") {
          setError(refreshed.message ?? "You are signed in, but we could not finish opening your workspace. Try again.");
          return;
        }

        if (refreshed.status === "unauthenticated") {
          setError("Sign-in succeeded, but we did not get a usable session back. Please try again.");
          return;
        }

        setError(refreshed.message);
      }
    } catch (err) {
      setError(friendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      setError("Enter your email address first.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotice(null);
    try {
      await authClient.signIn.magicLink({ email, callbackURL: from });
      setNotice("\u2709\uFE0F Check your email for a login link.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send magic link.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left: branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1040CC] to-[#0A2A8A] text-white flex-col justify-between p-12">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">stero.ai</h2>
          <p className="mt-1 text-white/50 text-sm">Revenue OS. With Ease.</p>
        </div>
        <div className="space-y-6">
          <blockquote className="text-lg font-light leading-relaxed text-white/80">
            &ldquo;One platform to run your entire marketing, sales, and revenue. So good, your competitors will think you hired a team of 50.&rdquo;
          </blockquote>
          <div className="flex gap-8 text-sm text-white/50">
            <div><span className="text-2xl font-bold text-white block">CRM</span>Zero data entry</div>
            <div><span className="text-2xl font-bold text-white block">AI</span>Works while you sleep</div>
            <div><span className="text-2xl font-bold text-white block">Revenue</span>Tracked to the penny</div>
          </div>
        </div>
        <p className="text-xs text-white/30">Join thousands of entrepreneurs building smarter businesses.</p>
      </div>

      {/* Right: login form */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 bg-[var(--color-background)]">
        <div className="w-full max-w-sm space-y-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 shadow-lg lg:border-0 lg:shadow-none lg:p-0">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-2">
            <h2 className="text-xl font-bold text-primary">stero.ai</h2>
          </div>

          <div className="text-left">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              &larr; Back to stero.ai
            </Link>
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
            <p className="mt-1 text-sm text-muted-foreground">Sign in to continue where you left off in setup, drafting, or editing.</p>
          </div>

          <SocialAuthButtons mode="signin" onError={setError} />

          <div className="flex items-center gap-4 my-2">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">or continue with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {(error || notice) && (
              <div
                className={`rounded-lg border p-3 text-sm ${
                  notice && isSuccessMessage(notice)
                    ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                    : notice
                      ? "border-primary/20 bg-primary/5 text-primary"
                    : "border-red-500/20 bg-red-500/5 text-red-600 dark:text-red-400"
                }`}
              >
                {notice ?? error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <button
            type="button"
            onClick={handleMagicLink}
            disabled={isLoading}
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Send magic link instead
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              Sign up free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
